import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import { createElement } from "@wordpress/element";
/**
 * External dependencies
 */

import memoize from 'memize';
import classnames from 'classnames';
import { camelCase, kebabCase, map, startCase } from 'lodash';
/**
 * WordPress dependencies
 */

import { __ } from '@wordpress/i18n';
import { useSelect, useDispatch } from '@wordpress/data';
import { useCallback, useMemo, Children, cloneElement } from '@wordpress/element';
/**
 * Internal dependencies
 */

import PanelColorSettings from '../panel-color-settings';
import ContrastChecker from '../contrast-checker';
import InspectorControls from '../inspector-controls';
import { useBlockEditContext } from '../block-edit';
var DEFAULT_COLORS = [];

var ColorPanel = function ColorPanel(_ref) {
  var title = _ref.title,
      colorSettings = _ref.colorSettings,
      colorPanelProps = _ref.colorPanelProps,
      contrastCheckerProps = _ref.contrastCheckerProps,
      components = _ref.components,
      panelChildren = _ref.panelChildren;
  return createElement(PanelColorSettings, _extends({
    title: title,
    initialOpen: false,
    colorSettings: Object.values(colorSettings)
  }, colorPanelProps), contrastCheckerProps && map(components, function (Component, key) {
    return createElement(ContrastChecker, _extends({
      key: key,
      textColor: colorSettings[key].value
    }, contrastCheckerProps));
  }), typeof panelChildren === 'function' ? panelChildren(components) : panelChildren);
};

var InspectorControlsColorPanel = function InspectorControlsColorPanel(props) {
  return createElement(InspectorControls, null, createElement(ColorPanel, props));
};

export default function __experimentalUseColors(colorConfigs) {
  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    panelTitle: __('Color Settings')
  },
      _ref2$panelTitle = _ref2.panelTitle,
      panelTitle = _ref2$panelTitle === void 0 ? __('Color Settings') : _ref2$panelTitle,
      colorPanelProps = _ref2.colorPanelProps,
      contrastCheckerProps = _ref2.contrastCheckerProps,
      panelChildren = _ref2.panelChildren;

  var deps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

  var _useBlockEditContext = useBlockEditContext(),
      clientId = _useBlockEditContext.clientId;

  var _useSelect = useSelect(function (select) {
    var _select = select('core/block-editor'),
        getBlockAttributes = _select.getBlockAttributes,
        getSettings = _select.getSettings;

    var colors = getSettings().colors;
    return {
      attributes: getBlockAttributes(clientId),
      settingsColors: !colors || colors === true ? DEFAULT_COLORS : colors
    };
  }, [clientId]),
      attributes = _useSelect.attributes,
      settingsColors = _useSelect.settingsColors;

  var _useDispatch = useDispatch('core/block-editor'),
      updateBlockAttributes = _useDispatch.updateBlockAttributes;

  var setAttributes = useCallback(function (newAttributes) {
    return updateBlockAttributes(clientId, newAttributes);
  }, [updateBlockAttributes, clientId]);
  var createComponent = useMemo(function () {
    return memoize(function (name, property, className, color, colorValue, customColor) {
      return function (_ref3) {
        var children = _ref3.children,
            _ref3$className = _ref3.className,
            componentClassName = _ref3$className === void 0 ? '' : _ref3$className,
            _ref3$style = _ref3.style,
            componentStyle = _ref3$style === void 0 ? {} : _ref3$style;
        return (// Clone children, setting the style property from the color configuration,
          // if not already set explicitly through props.
          Children.map(children, function (child) {
            var _classnames;

            var colorStyle = {};

            if (color) {
              colorStyle = _defineProperty({}, property, colorValue);
            } else if (customColor) {
              colorStyle = _defineProperty({}, property, customColor);
            }

            return cloneElement(child, {
              className: classnames(componentClassName, child.props.className, (_classnames = {}, _defineProperty(_classnames, "has-".concat(kebabCase(color), "-").concat(kebabCase(property)), color), _defineProperty(_classnames, className || "has-".concat(kebabCase(name)), color || customColor), _classnames)),
              style: _objectSpread({}, colorStyle, componentStyle, child.props.style || {})
            });
          })
        );
      };
    }, {
      maxSize: colorConfigs.length
    });
  }, [colorConfigs.length]);
  var createSetColor = useMemo(function () {
    return memoize(function (name, colors) {
      return function (newColor) {
        var color = colors.find(function (_color) {
          return _color.color === newColor;
        });
        setAttributes(_defineProperty({}, color ? camelCase("custom ".concat(name)) : name, undefined));
        setAttributes(_defineProperty({}, color ? name : camelCase("custom ".concat(name)), color ? color.slug : newColor));
      };
    }, {
      maxSize: colorConfigs.length
    });
  }, [setAttributes, colorConfigs.length]);
  return useMemo(function () {
    var colorSettings = {};
    var components = colorConfigs.reduce(function (acc, colorConfig) {
      if (typeof colorConfig === 'string') {
        colorConfig = {
          name: colorConfig
        };
      }

      var _colorConfig$color = _objectSpread({}, colorConfig, {
        color: attributes[colorConfig.name]
      }),
          name = _colorConfig$color.name,
          _colorConfig$color$pr = _colorConfig$color.property,
          property = _colorConfig$color$pr === void 0 ? name : _colorConfig$color$pr,
          className = _colorConfig$color.className,
          _colorConfig$color$pa = _colorConfig$color.panelLabel,
          panelLabel = _colorConfig$color$pa === void 0 ? startCase(name) : _colorConfig$color$pa,
          _colorConfig$color$co = _colorConfig$color.componentName,
          componentName = _colorConfig$color$co === void 0 ? panelLabel.replace(/\s/g, '') : _colorConfig$color$co,
          _colorConfig$color$co2 = _colorConfig$color.color,
          color = _colorConfig$color$co2 === void 0 ? colorConfig.color : _colorConfig$color$co2,
          _colorConfig$color$co3 = _colorConfig$color.colors,
          colors = _colorConfig$color$co3 === void 0 ? settingsColors : _colorConfig$color$co3; // We memoize the non-primitives to avoid unnecessary updates
      // when they are used as props for other components.


      var _color = colors.find(function (__color) {
        return __color.slug === color;
      });

      acc[componentName] = createComponent(name, property, className, color, _color && _color.color, attributes[camelCase("custom ".concat(name))]);
      acc[componentName].displayName = componentName;
      acc[componentName].color = color;
      acc[componentName].setColor = createSetColor(name, colors);
      colorSettings[componentName] = {
        value: _color ? _color.color : attributes[camelCase("custom ".concat(name))],
        onChange: acc[componentName].setColor,
        label: panelLabel,
        colors: colors
      }; // These settings will be spread over the `colors` in
      // `colorPanelProps`, so we need to unset the key here,
      // if not set to an actual value, to avoid overwriting
      // an actual value in `colorPanelProps`.

      if (!colors) {
        delete colorSettings[componentName].colors;
      }

      return acc;
    }, {});
    var wrappedColorPanelProps = {
      title: panelTitle,
      colorSettings: colorSettings,
      colorPanelProps: colorPanelProps,
      contrastCheckerProps: contrastCheckerProps,
      components: components,
      panelChildren: panelChildren
    };
    return _objectSpread({}, components, {
      ColorPanel: createElement(ColorPanel, wrappedColorPanelProps),
      InspectorControlsColorPanel: createElement(InspectorControlsColorPanel, wrappedColorPanelProps)
    });
  }, [attributes, setAttributes].concat(_toConsumableArray(deps)));
}